import {
  CommissionSplitWithSecondaryAgent,
  useStaffGetCommissionInfoForCampaign,
  CommissionDetails,
  StaffUpdateCommissionInfoForCampaignRequestChangeType,
  useStaffUpdateCommissionInfoForCampaign,
  UploadedDocument,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxCommissionTableView,
  AgxSpacer,
  AgxTypography,
  Icon_EditOutline,
  AgxCommissionSplit,
  SAVE_BUTTON_STATES,
  AgxSaveChangesButton,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import { CommissionMenu, FinanceMenu, MenuTitles } from 'constants/menu'
import { useAgentsInAgency, useSelectedCampaign } from 'hooks'
import { ContentLayout } from 'layout'
import CommissionHistoryModal from './CommissionHistoryModal'
import { useCallback, useEffect, useMemo, useState } from 'react'
import EditCommissionModal from './EditCommissionModal'
import { AgxToastState } from 'types/commonTypes'
import LoadingState from 'components/loadingState/LoadingState'
import { isDirtyEqual } from 'utils/compares'
import { AgxToast } from '@urbanx/agx-ui-components'

const CommissionPage = () => {
  const { campaign: selectedCampaign } = useSelectedCampaign()
  const { data: agentsInAgency } = useAgentsInAgency()
  const [commissionDetails, setCommissionDetails] = useState<
    CommissionDetails | undefined
  >(undefined)
  const [commissionSplit, setCommissionSplit] = useState<
    CommissionSplitWithSecondaryAgent | undefined
  >(undefined)
  const [isCommissionHistoryModalOpen, setIsCommissionHistoryModalOpen] =
    useState(false)
  const [isEditCommissionModalOpen, setIsEditCommissionModalOpen] =
    useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isError, setIsError] = useState(false)
  const [hasSaved, setHasSaved] = useState(false)
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  })

  const {
    data: commissionInformation,
    isPending: commissionInformationPending,
  } = useStaffGetCommissionInfoForCampaign(
    selectedCampaign?.campaignId || '',
    !!selectedCampaign
  )

  useEffect(() => {
    setCommissionDetails(
      commissionInformation?.commissionInformation?.currentCommissionDetails
        ?.commissionDetails
    )
    setCommissionSplit(commissionInformation?.commissionSplitWithSecondaryAgent)
    setIsLoaded(!commissionInformationPending)
  }, [commissionInformation, commissionInformationPending])

  const {
    staffUpdateCommissionInfoForCampaign,
    staffUpdateCommissionInfoForCampaignPending,
  } = useStaffUpdateCommissionInfoForCampaign({
    onSuccess: async () => {},
    onError: (error) => {
      console.error('Error updating commission info:', error)
    },
  })

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(
      commissionSplit,
      commissionInformation?.commissionSplitWithSecondaryAgent
    )
  }, [
    commissionSplit,
    commissionInformation?.commissionSplitWithSecondaryAgent,
  ])

  const savingState = useMemo(() => {
    if (isSaving) return SAVE_BUTTON_STATES.SAVING
    if (hasSaved) return SAVE_BUTTON_STATES.SAVED
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED
    if (isError) return SAVE_BUTTON_STATES.ERROR
    return SAVE_BUTTON_STATES.UNSAVED
  }, [isSaving, isError, isFormDirty, hasSaved])

  useEffect(() => {
    if (isFormDirty) {
      setHasSaved(false)
    }
  }, [isFormDirty])

  useEffect(() => {
    if (savingState === SAVE_BUTTON_STATES.SAVED) {
      updateToastState({
        color: 'success',
        message: 'Changes saved successfully',
        open: true,
      })
    }
  }, [savingState])

  const onCommissionSplitChange = useCallback(
    (updatedCommissionSplit: CommissionSplitWithSecondaryAgent) => {
      setCommissionSplit(updatedCommissionSplit)
    },
    [setCommissionSplit]
  )

  const onCommissionDetailsSave = useCallback(
    async ({
      updatedCommission,
      reasonType,
      reason,
      changeEvidence,
    }: {
      updatedCommission: CommissionDetails
      reasonType: StaffUpdateCommissionInfoForCampaignRequestChangeType
      reason?: string
      changeEvidence?: UploadedDocument
    }) => {
      if (!selectedCampaign?.campaignId) return false

      try {
        await staffUpdateCommissionInfoForCampaign({
          campaignId: selectedCampaign.campaignId,
          commissionDetails: updatedCommission,
          changeType: reasonType,
          reason,
          changeEvidence,
          commissionSplitWithSecondaryAgent: commissionSplit,
        })
        return true
      } catch (error) {
        console.error('Error updating commission info:', error)
        updateToastState({
          color: 'error',
          message: 'Error saving changes. Please try again.',
          open: true,
        })
        return false
      }
    },
    [
      selectedCampaign?.campaignId,
      staffUpdateCommissionInfoForCampaign,
      commissionSplit,
    ]
  )

  const onCommissionSplitSave = useCallback(async () => {
    if (!selectedCampaign?.campaignId) return

    setIsSaving(true)
    setIsError(false)

    await staffUpdateCommissionInfoForCampaign(
      {
        campaignId: selectedCampaign.campaignId,
        commissionSplitWithSecondaryAgent: commissionSplit,
      },
      {
        onSuccess: async () => {
          setIsSaving(false)
          setHasSaved(true)
        },
        onError: () => {
          updateToastState({
            color: 'error',
            message: 'Error saving changes. Please try again.',
            open: true,
          })
          setIsError(true)
          setIsSaving(false)
        },
      }
    )
  }, [
    selectedCampaign?.campaignId,
    commissionSplit,
    staffUpdateCommissionInfoForCampaign,
  ])

  return (
    <ContentLayout
      hasSecondaryMenu={false}
      secondaryActiveMenu={MenuTitles.COMMISSION}
      secondaryMenuOverride={FinanceMenu}
      isCampaigns={true}
      tertiaryMenu={CommissionMenu}
      tertiaryActiveMenu={MenuTitles.COMMISSION_COMMISSION}
      tertiaryTitle={'Commission'}
      tertiaryActions={
        <AgxSaveChangesButton
          id="referralConjunctionSaveChanges"
          text="Save Changes"
          size="small"
          variant="primary"
          state={
            staffUpdateCommissionInfoForCampaignPending
              ? SAVE_BUTTON_STATES.SAVING
              : savingState
          }
          onClick={onCommissionSplitSave}
          disabled={!isFormDirty || staffUpdateCommissionInfoForCampaignPending}
        />
      }
    >
      <AgxToast selector="#agxToast" toastState={toastState} />
      {!isLoaded ? (
        <LoadingState />
      ) : (
        <>
          {isCommissionHistoryModalOpen && (
            <CommissionHistoryModal
              onClose={() => setIsCommissionHistoryModalOpen(false)}
              commission={commissionInformation?.commissionInformation}
            />
          )}

          {isEditCommissionModalOpen && (
            <EditCommissionModal
              currentCommissionDetails={commissionDetails}
              campaign={selectedCampaign}
              isSaving={staffUpdateCommissionInfoForCampaignPending}
              onSave={onCommissionDetailsSave}
              onClose={() => setIsEditCommissionModalOpen(false)}
            />
          )}

          <AgxSpacer gap="spacing-5xl" fill>
            <Panel
              title={
                <AgxTypography id="panel" type="title" size="large">
                  Commission
                </AgxTypography>
              }
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <AgxSpacer gap="spacing-3xl" fill>
                {!commissionInformationPending && (
                  <>
                    {commissionDetails !== undefined && (
                      <AgxCommissionTableView
                        currentCommissionDetails={commissionDetails}
                      />
                    )}
                    <AgxSpacer
                      gap="spacing-xl"
                      fill
                      direction="horizontal"
                      contentAlignment="center"
                      contentJustification="end"
                    >
                      {commissionDetails !== undefined && (
                        <AgxButton
                          id={'viewCommissionHistory'}
                          text={'View history'}
                          variant={'hollow'}
                          size="small"
                          onClick={() => setIsCommissionHistoryModalOpen(true)}
                        />
                      )}
                      <AgxButton
                        id={'editCommission'}
                        text={'Edit'}
                        variant={'hollow'}
                        size="small"
                        rightIcon={
                          <Icon_EditOutline color="var(--fg-brand-accent)" />
                        }
                        onClick={() => setIsEditCommissionModalOpen(true)}
                      />
                    </AgxSpacer>
                  </>
                )}
              </AgxSpacer>
            </Panel>

            <Panel
              title={
                <AgxTypography id="panel" type="title" size="large">
                  Commission Split
                </AgxTypography>
              }
              padding={{
                top: 'spacing-3xl',
                bottom: 'spacing-3xl',
                left: 'spacing-5xl',
                right: 'spacing-5xl',
              }}
            >
              <AgxSpacer gap="spacing-3xl" fill>
                {!commissionInformationPending && (
                  <AgxCommissionSplit
                    id="commission-split-page"
                    agents={agentsInAgency}
                    leadAgentId={selectedCampaign?.leadAgentId}
                    secondAgentId={selectedCampaign?.secondAgentId}
                    commissionSplitWithSecondaryAgent={commissionSplit}
                    onChange={onCommissionSplitChange}
                  />
                )}
              </AgxSpacer>
            </Panel>
          </AgxSpacer>
        </>
      )}
    </ContentLayout>
  )
}

export default CommissionPage
